// @flow
import {List, fromJS} from 'immutable';
import {Store} from 'client/framework';
import {getResource, getResourceMetadata, isResourcePopulated} from 'resources/mandark.resource';
import {AssignmentModelV2} from 'resources/Assignment/V2/Assignment.model';
import {mandarkEndpoint} from '@albert-io/json-api-framework/request/builder/legacy';
import appStore from 'client/AppStore';

import PaginationStore from 'client/generic/PaginationV2/Pagination.store';

import assignmentOverviewActions from './AssignmentOverview.actions';

const DUE_DATE_SORT_FIELD = 'due_date';
const NAME_SORT_FIELD = 'name';
const SUBMISSION_STATUS_SORT_FIELD = 'meta.student_submitted,meta.student_late';

// This works for us because we are negating things across the compound sort together
function makeSortFieldDesc(field): string {
  return field
    .split(',')
    .map((field) => `-${field}`)
    .join(',');
}

const sortDefaults = fromJS({
  [DUE_DATE_SORT_FIELD]: {sortDirection: 'desc'},
  [NAME_SORT_FIELD]: {sortDirection: 'asc'},
  [SUBMISSION_STATUS_SORT_FIELD]: {sortDirection: 'desc'}
});

const PAGE_SIZE = 13;
const PAGINATION_STORE_NAME = 'AssignmentOverviewTableStorePaginator';

class AssignmentOverviewTableStore extends Store {
  constructor(name: string) {
    super(name);

    this.initialData = fromJS({
      sortField: DUE_DATE_SORT_FIELD,
      sortDirection: sortDefaults.getIn([DUE_DATE_SORT_FIELD, 'sortDirection'])
    });
    this.setInitialData(this.initialData);

    this._paginatorStore = new PaginationStore(PAGINATION_STORE_NAME);

    this.handle(assignmentOverviewActions.APPLY_SORT, this._applySort);
  }

  getPaginatorStore(): PaginationStore {
    return this._paginatorStore;
  }

  _applySort(sortField: string) {
    this.writeData((state) => {
      const isNewField = sortField !== this.getSortField();
      const newSortDirection = isNewField
        ? sortDefaults.getIn([sortField, 'sortDirection']) // Take default of new field
        : this.getSortDirection() === 'asc'
        ? 'desc'
        : 'asc'; // Flip directions
      return state.set('sortField', sortField).set('sortDirection', newSortDirection);
    });
  }

  _getAssignmentQuery(): Object {
    return mandarkEndpoint(['students_v2', appStore.routerProps.params.userId, 'assignments_v3'])
      .include('students_v2')
      .include('sections_v1')
      .filter({
        classrooms_v1: appStore.routerProps.params.classId
      })
      .filter({
        included: {
          students_v2: {
            id: appStore.routerProps.params.userId
          }
        }
      })
      .customQuery({
        page: {
          page: this.getPaginatorStore().activePage,
          page_size: PAGE_SIZE
        },
        sort:
          this.getSortDirection() === 'desc'
            ? makeSortFieldDesc(this.getSortField())
            : this.getSortField(),
        with_meta: 'assignment_v3'
      })
      .meta({
        context: {
          student: {
            id: appStore.routerProps.params.userId
          }
        }
      })
      .done();
  }

  getTotalPages(): number {
    return getResourceMetadata(this._getAssignmentQuery()).getIn(['page', 'total_pages'], 0);
  }

  isPaginated(): boolean {
    return this.getTotalPages() > 1;
  }

  getAssignmentData(): List<AssignmentModelV2> {
    return getResource(this._getAssignmentQuery());
  }

  hasAssignmentData(): boolean {
    return isResourcePopulated(this._getAssignmentQuery());
  }

  getSortField(): string {
    return this.readData('sortField');
  }

  getSortDirection(): string {
    return this.readData('sortDirection');
  }

  getDueDateSortField(): string {
    return DUE_DATE_SORT_FIELD;
  }

  getNameSortField(): string {
    return NAME_SORT_FIELD;
  }

  getSubmissionStatusSortField(): string {
    return SUBMISSION_STATUS_SORT_FIELD;
  }
}

export default new AssignmentOverviewTableStore('AssignmentOverviewTableStore');
